<template>
  <NotFound
    v-if="!product"
  />
  <Product v-else />
</template>

<script setup>
import { onServerPrefetch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { loadTagLibrary } from '@vue-util/tagManager';
import { usePageData } from '@composables/usePageData';
import { useEnvProps } from '@composables/useEnvProps';
import { useProductProvide, useQuantityProvide } from '@modules/product';
import { useFeatureEligibility } from '@modules/featureControl';
import { usePpcProvide } from '@modules/ppc';
import { useUserProvide } from '@composables/user/useUser';
import { useLoyaltyProvide } from '@composables/useLoyalty';
import { useUpcValidatorProvide, useUpcPreselect } from '@modules/upc';
import { useImagesProvide } from '@composables/useImages';
import { useFulfilmentDataProvide, useBigTicketATBValidatorProvide, useAvailabilityBigTicketProvide } from '@modules/availability';
import { useBopsDataProvide } from '@composables/useBopsData';
import { useMarxentProvide } from '@composables/useMarxent';
import { useVoteProvide } from '@composables/reviews/useVotes';
import { useProvideUrlLocationOperations } from '@composables/useUrlLocationOperations';
import { useProductPriceProvide } from '@modules/price/composables/useProductPriceProvide';
import { fireViewTag } from '../features/analytics/viewManager';
import Product from '../components/Product';

import NotFound from '../components/NotFound';
// import NotEligible from '../components/NotEligible';

// import { useProductFlags } from '@modules/featureControl';
// import { useCommonProductGetters } from '../composables/useCommonProductGetters';

const store = useStore();
const {
  productId, product, experimentIds,
} = usePageData();
// const { isCountryEligibleFlag } = useProductFlags();
if (product) {
  useProvideUrlLocationOperations();
  useUserProvide();
  useLoyaltyProvide();
  useProductProvide(productId, product?.product);
  useProductPriceProvide({ id: productId, product: product?.product });
  useQuantityProvide();
  useUpcValidatorProvide();
  useImagesProvide();
  useFulfilmentDataProvide();
  useBopsDataProvide();
  useVoteProvide();
  useMarxentProvide();
  useAvailabilityBigTicketProvide();
  useBigTicketATBValidatorProvide();

  useUpcPreselect();
  usePpcProvide();
  /**
   * @description: check to see if the default salesfloor widget should be hidden
   * @note: this should only be called once all composables are provided and the product is available
   * */
  const { isSalesFloorVisible } = useFeatureEligibility();

  // append body class on mount
  onMounted(() => {
    // only if on mcom and homeFOB category product (except for products with category id 22672) OR gift card product
    if (!isSalesFloorVisible) {
      document.body.classList.add('otb-widget--hide');
    }
  });
}

onMounted(async () => {
  const { brand, tagMngrEnv, tagMngrEnabled } = useEnvProps();
  if (tagMngrEnabled) {
    const isAutoTaggingEnabled = product?.killswitches?.isAutoTaggingEnabled && experimentIds?.includes('1220-23');
    if (!isAutoTaggingEnabled) {
      await loadTagLibrary(brand, tagMngrEnv, true); // Manually firing view tag and setting Tealium not to fire
    }
    const { ExperimentationTags } = require('@vue-util/experimentation');
    await ExperimentationTags.fireAdobeTags();
  }
});

onMounted(fireViewTag);

onServerPrefetch(async () => {
  await Promise.allSettled([
    store.dispatch('productStore/init', { productId }),
  ]);
});

</script>

<style lang="scss" scoped>
// salesfloor out of the box hidden
.otb-widget--hide {
  #sf-widget-companion-wrapper {
    display: none !important;
  }
}
</style>
